import { motion } from "framer-motion";
import { Car, Heart, Music, Clock, Star, Users, Building, Sparkles } from "lucide-react";
import { FaWhatsapp } from "react-icons/fa";
import { useLanguage } from "../context/LanguageContext";

const ServicesSection = () => {
  const { t } = useLanguage();
  const whatsappNumber = "+905488484828";

  const getWhatsappLink = (service) => {
    const message = encodeURIComponent(`${t("floatingWhatsapp.defaultMessage")} - ${service}`);
    return `https://wa.me/${whatsappNumber.replace(/\+/g, "")}?text=${message}`;
  };

  const services = [
    {
      id: 1,
      titleKey: "services.service1Title",
      descKey: "services.service1Desc",
      featuresKey: "services.service1Features",
      icon: Heart,
      image: "https://images.unsplash.com/photo-1581197326868-51cafbd1a169?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA2OTV8MHwxfHNlYXJjaHwxfHxwcml2YXRlJTIwcGFydHklMjBkYW5jZXIlMjBzaWxob3VldHRlfGVufDB8fHx8MTc3OTQwNzkxMnww&ixlib=rb-4.1.0&q=85&w=600",
      color: "#FF00FF"
    },
    {
      id: 2,
      titleKey: "services.service2Title",
      descKey: "services.service2Desc",
      featuresKey: "services.service2Features",
      icon: Music,
      image: "https://images.unsplash.com/photo-1627224399635-04b73da49323?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2NzB8MHwxfHNlYXJjaHw0fHxwb2xlJTIwZGFuY2UlMjBwZXJmb3JtYW5jZSUyMHN0YWdlJTIwbGlnaHRzfGVufDB8fHx8MTc3OTQwNzkwOHww&ixlib=rb-4.1.0&q=85&w=600",
      color: "#D4AF37"
    },
    {
      id: 3,
      titleKey: "services.service3Title",
      descKey: "services.service3Desc",
      featuresKey: "services.service3Features",
      icon: Car,
      image: "https://images.unsplash.com/photo-1771775751121-3091d79073d4?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDk1NzZ8MHwxfHNlYXJjaHwzfHxsdXh1cnklMjBWSVAlMjBsaW1vdXNpbmUlMjBuaWdodHxlbnwwfHx8fDE3Nzk0MDc5MDZ8MA&ixlib=rb-4.1.0&q=85&w=600",
      color: "#00FFFF"
    }
  ];

  const stats = [
    { number: "10+", labelKey: "services.stat1", icon: Clock },
    { number: "50K+", labelKey: "services.stat2", icon: Users },
    { number: "5", labelKey: "services.stat3", icon: Star }
  ];

  return (
    <section id="services" className="py-24 bg-[#050505] relative" data-testid="services-section">
      {/* Section Header */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 mb-16">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="text-center"
        >
          <span className="text-xs font-mono tracking-[0.3em] text-[#FF00FF] uppercase mb-4 block">
            {t("services.subtitle")}
          </span>
          <h2 className="font-['Syne'] text-4xl md:text-5xl lg:text-6xl font-bold tracking-tight uppercase mb-6">
            <span className="text-white">{t("services.title1")}</span>{" "}
            <span className="gradient-text">{t("services.title2")}</span>
          </h2>
          <p className="text-gray-400 max-w-2xl mx-auto text-lg">
            {t("services.description")}
          </p>
        </motion.div>
      </div>

      {/* Stats */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 mb-20">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="grid grid-cols-3 gap-4 md:gap-8"
        >
          {stats.map((stat, index) => (
            <div
              key={index}
              className="text-center p-6 glass"
              data-testid={`stat-${index}`}
            >
              <stat.icon className="w-6 h-6 mx-auto mb-3 text-[#D4AF37]" />
              <div className="font-['Syne'] text-3xl md:text-4xl font-bold text-white mb-1">
                {stat.number}
              </div>
              <div className="text-xs md:text-sm text-gray-400 tracking-wider uppercase">
                {t(stat.labelKey)}
              </div>
            </div>
          ))}
        </motion.div>
      </div>

      {/* Highlighted Service - Hotel/Villa Service */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 mb-12">
        <motion.div
          initial={{ opacity: 0, y: 50 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="relative overflow-hidden border-2 border-[#FF00FF] bg-gradient-to-r from-[#FF00FF]/10 to-transparent"
          data-testid="highlighted-service"
        >
          <div className="grid md:grid-cols-2 gap-0">
            {/* Image */}
            <div className="relative h-64 md:h-auto overflow-hidden">
              <img
                src="https://images.unsplash.com/photo-1776763018970-9fdf66bd4666?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2NzF8MHwxfHNlYXJjaHwyfHxsdXh1cnklMjBob3RlbCUyMHN1aXRlJTIwbmlnaHQlMjByb21hbnRpY3xlbnwwfHx8fDE3Nzk0MDc5MDl8MA&ixlib=rb-4.1.0&q=85&w=800"
                alt="Otel Villa Rezidans Konsomatris Dansöz Hizmeti"
                className="w-full h-full object-cover"
              />
              <div className="absolute inset-0 bg-gradient-to-r from-[#050505] via-transparent to-transparent" />
              <div className="absolute top-4 left-4 bg-[#FF00FF] text-white px-4 py-2 font-bold text-sm uppercase tracking-wider">
                {t("services.mostPopular")}
              </div>
            </div>
            
            {/* Content */}
            <div className="p-8 md:p-12 flex flex-col justify-center">
              <div className="flex items-center gap-3 mb-4">
                <Building size={32} className="text-[#FF00FF]" />
                <h3 className="font-['Syne'] text-2xl md:text-3xl font-bold uppercase text-white">
                  {t("services.highlightedTitle")}
                </h3>
              </div>
              <p className="text-gray-300 mb-6 text-lg leading-relaxed">
                {t("services.highlightedDesc")}
              </p>
              
              {/* Features */}
              <div className="grid grid-cols-2 gap-3 mb-8">
                {t("services.highlightedFeatures").map((feature, i) => (
                  <div key={i} className="flex items-center gap-2 text-sm text-gray-300">
                    <span className="w-2 h-2 bg-[#FF00FF]"></span>
                    {feature}
                  </div>
                ))}
              </div>

              <a
                href={getWhatsappLink(t("services.highlightedTitle"))}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center justify-center gap-3 bg-[#FF00FF] text-white px-8 py-4 font-bold text-lg tracking-wider uppercase hover:scale-105 transition-all duration-300 btn-clip w-full md:w-auto"
                data-testid="highlighted-service-cta"
              >
                <FaWhatsapp size={24} />
                <span>{t("services.callNow")}</span>
              </a>
            </div>
          </div>
        </motion.div>
      </div>

      {/* Services Grid */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {services.map((service, index) => (
            <motion.div
              key={service.id}
              initial={{ opacity: 0, y: 50 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: index * 0.1 }}
              className="service-card relative bg-white/5 border border-white/10 overflow-hidden group"
              data-testid={`service-card-${service.id}`}
            >
              {/* Image */}
              <div className="relative overflow-hidden h-56">
                <img
                  src={service.image}
                  alt={t(service.titleKey)}
                  className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-[#050505] via-transparent to-transparent" />
                
                {/* Icon Badge */}
                <div
                  className="absolute top-4 left-4 w-12 h-12 flex items-center justify-center"
                  style={{ backgroundColor: service.color }}
                >
                  <service.icon size={24} className="text-black" />
                </div>
              </div>

              {/* Content */}
              <div className="p-6">
                <h3 className="font-['Syne'] text-xl md:text-2xl font-bold uppercase mb-3 text-white group-hover:text-[#FF00FF] transition-colors">
                  {t(service.titleKey)}
                </h3>
                <p className="text-gray-400 text-sm mb-4 leading-relaxed">
                  {t(service.descKey)}
                </p>

                {/* Features */}
                <div className="flex flex-wrap gap-2 mb-6">
                  {t(service.featuresKey).map((feature, i) => (
                    <span
                      key={i}
                      className="text-xs px-3 py-1 bg-white/5 border border-white/10 text-gray-300"
                    >
                      {feature}
                    </span>
                  ))}
                </div>

                {/* CTA */}
                <a
                  href={getWhatsappLink(t(service.titleKey))}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 text-sm font-medium tracking-wider uppercase transition-colors"
                  style={{ color: service.color }}
                  data-testid={`service-cta-${service.id}`}
                >
                  <FaWhatsapp size={18} />
                  <span>{t("services.bookNow")}</span>
                  <span className="group-hover:translate-x-1 transition-transform">→</span>
                </a>
              </div>

              {/* Hover border glow */}
              <div
                className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-500 pointer-events-none"
                style={{
                  boxShadow: `inset 0 0 30px ${service.color}20`
                }}
              />
            </motion.div>
          ))}
        </div>
      </div>

      {/* SEO Text Section */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 mt-20">
        <div className="text-center text-gray-500 text-sm leading-relaxed max-w-4xl mx-auto">
          <p>{t("services.seoText")}</p>
        </div>
      </div>

      {/* Decorative line */}
      <div className="section-divider mt-24 max-w-4xl mx-auto" />
    </section>
  );
};

export default ServicesSection;
